import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { SchemaService } from '../services/schema.service';
import { Field } from '../models/field.model';
import { Schema } from '../models/schema.model';
import { AppConfigProvider } from '../../../app-config.provider';
import { DefaultFieldService } from '../services/default-field.service';
import { map, flatMap } from 'rxjs/operators';
import { ContentFactory } from './content.factory';
import { DynamicContentModel } from '../../akita/dynamic-content-schema/dynamic-content.model';
import { FieldReferenceCleaner } from '../../../shared/field/tools/field-reference-cleaner';
import { AppSchemaCategory } from '../../../models/app-token';

@Injectable({ providedIn: 'root' })
export class SchemasFactory {
  public readonly baseUrl: string;
  private defaultFieldService: DefaultFieldService;
  public reloadSchemas: boolean = false;

  schemaAsPageHiddenFields: string[] = ['pageName', 'metatitle',
    'url', 'metaDescription',
    'metaKeywords'];

  constructor(
    private readonly httpClient: HttpClient,
    appConfigProvider: AppConfigProvider,
    private contentFactory: ContentFactory
  ) {
    this.baseUrl = appConfigProvider.getSchemaBaseUrl();
  }

  getSchemaService(schemaName: string): SchemaService {
    return new SchemaService(this.httpClient, this.baseUrl, schemaName);
  }

  //! Hard debug function, should never be used, never be refered, can be usefull, don't delete
  initSchemasDefaultFields() {
    this.getAllSchemas().subscribe(schemas => {
      this.initDefaultFieldService();
      const defaultField = this.defaultFieldService.generateDefaultFields();
      schemas.forEach(metaSchema => {
        const schemaService = this.getSchemaService(metaSchema.name);
        schemaService.getSchemaDefinition().subscribe(schema => {

          let fieldsNeeded: Field[];

          if (schema.fields) {
            fieldsNeeded = defaultField
              .filter(field => !schema.fields.some(schemaField => schemaField.name === field.name));
          } else {
            fieldsNeeded = defaultField;
          }

          fieldsNeeded.forEach(fieldNeeded => {
            schemaService.addField(fieldNeeded).subscribe();
          });

        });
      });
    });
  }

  hardDeleteSchema(id: string): Promise<Object> {
    return this.httpClient.delete(`${this.baseUrl}/schemas/${id}`).toPromise().then();
  }

  createSchema(schema: Schema): Observable<Schema> {
    const url = `${this.baseUrl}/schemas/`;
    if (!schema.category || schema.category === '') {
      throw (new Error('Your schema MUST have a valid category'));
    }

    this.initDefaultFieldService();
    let defaultFields = this.defaultFieldService.generateFields(schema.category);

    return this.getAllSchemas().pipe(flatMap(allSchema => {
      const cleaner = new FieldReferenceCleaner(allSchema);
      defaultFields = cleaner.cleanAllReferenceFields(defaultFields);
      if (!schema.fields) {
        schema.fields = defaultFields;
      } else {
        defaultFields.forEach((field) => {
          if (!schema.fields.some(f => f.name === field.name)) {
            schema.fields.push(field);
          }
        });
      }

      const oSchema = this.httpClient.post(url, schema) as Observable<Schema>;
      const contentService = this.contentFactory.getContentService('schemametadata');

      let hiddenFields = '';
      let defaultHiddenFields = defaultFields;
      if (schema.category === AppSchemaCategory.SCHEMA_AS_A_PAGE) {
        defaultHiddenFields = defaultHiddenFields.filter(field => !this.schemaAsPageHiddenFields.includes(field.name));
      }

      defaultHiddenFields.forEach(field => {
        hiddenFields += field.name + ';';
      });
      hiddenFields = hiddenFields.substring(0, hiddenFields.length - 1);

      let securedSchema = false;
      if(schema.category == AppSchemaCategory.SYSTEM_SCHEMAS) {
        securedSchema = true;
      }

      return oSchema.pipe(map(sch => {
        const meta = new DynamicContentModel();
        meta.data = {
          schemaid: { iv: sch.id },
          code: { iv: sch.name },
          category: { iv: sch.category },
          hiddenfields: { iv: hiddenFields },
          secured: { iv: securedSchema }
        };

        contentService.createContentAndPublish(meta.data).subscribe();
        this.httpClient.put(`${url}${schema.name}/publish`, {}).subscribe(schemaBack => {
          this.getAllSchemas().subscribe();
        });
        return sch;
      }));
    }));
  }

  // TODO: store schemas list in AKITA as an individual list
  getAllSchemas(): Observable<Schema[]> {
    if (this.schemas && !this.reloadSchemas) {
      return of(this.schemas);
    } else {
      const url = `${this.baseUrl}/schemas/`;
      return this.httpClient.get(url).pipe(map(schemasList => {
        this.schemas = schemasList['items'];
        this.reloadSchemas = false;
        return this.schemas;
      }));
    }
  }

  // tslint:disable-next-line: member-ordering
  private schemas: Schema[];

  private initDefaultFieldService() {
    if (!this.defaultFieldService) {
      this.defaultFieldService = new DefaultFieldService();
    }
  }

  //! Hard debug function, should never be used, never be refered, can be usefull, don't delete
  regenarateMetadata() {
    this.getAllSchemas().subscribe(allSchemas => {
      const service = this.contentFactory.getContentService('schemametadata');
      const defaultFieldService = new DefaultFieldService();

      allSchemas.forEach(schema => {

        service.createContent({
          "code": { 'iv': schema.name },
          "schemaid": { 'iv': schema.id },
          "hiddenfields": { 'iv': defaultFieldService.generateDefaultFields().map(field => field.name).join(';') },
          "category": { 'iv': schema.category }
        }).subscribe();
      });
    });
  }
}
